<template>
  <div class="api-client-page full-height">
    <table-con>
      <div slot="breadcrumb">
        <el-breadcrumb separator-class="el-icon-arrow-right">
          <el-breadcrumb-item :to="{ path: '/orders' }">首页</el-breadcrumb-item>
          <el-breadcrumb-item>系统管理</el-breadcrumb-item>
          <el-breadcrumb-item>API客户端</el-breadcrumb-item>
        </el-breadcrumb>
      </div>
      <div slot="search">
        <el-row class="search-row">
          <el-col :span="8">
            <el-input v-model="query.name" clearable size="small" placeholder="客户端名称或Client ID" @keyup.enter.native="load" />
          </el-col>
          <el-col :span="16" class="actions">
            <el-button class="search-btn btn-gray" @click="query.name=''; load()">重置</el-button>
            <el-button class="search-btn btn-blue" @click="load">搜索</el-button>
            <el-button class="search-btn btn-blue" @click="openCreate">新增客户端</el-button>
          </el-col>
        </el-row>
      </div>
      <div slot="table" class="client-table display-col full-height">
        <el-table :data="rows" border :height="tableResize.tableHeight" :header-cell-style="tableStyle.headerStyle" :cell-style="tableStyle.cellStyle">
          <el-table-column prop="clientName" label="客户端名称" min-width="140" />
          <el-table-column prop="clientId" label="Client ID" min-width="210" />
          <el-table-column prop="clientSecret" label="Client Secret" min-width="260" show-overflow-tooltip />
          <el-table-column prop="redirectUri" label="回调地址" min-width="220" show-overflow-tooltip />
          <el-table-column label="Scope" min-width="260">
            <template slot-scope="scope">
              <el-tag v-for="item in parseScopes(scope.row.scope)" :key="item" size="mini" class="scope-tag">{{ item }}</el-tag>
            </template>
          </el-table-column>
          <el-table-column label="有效期" width="180">
            <template slot-scope="scope">Access {{ scope.row.accessTokenExpire }}秒<br>Refresh {{ scope.row.refreshTokenExpire }}秒</template>
          </el-table-column>
          <el-table-column label="状态" width="80">
            <template slot-scope="scope">{{ scope.row.status === '0' ? '启用' : '停用' }}</template>
          </el-table-column>
          <el-table-column label="操作" fixed="right" width="210" align="center">
            <template slot-scope="scope">
              <el-button size="mini" icon="el-icon-edit" circle title="编辑" @click="openEdit(scope.row)" />
              <el-button size="mini" type="primary" icon="el-icon-user" circle title="授权Token" @click="showAuthorizedTokens(scope.row)" />
              <el-button size="mini" type="warning" icon="el-icon-refresh" circle title="重置密钥" @click="resetSecret(scope.row)" />
              <el-button size="mini" type="danger" icon="el-icon-delete" circle title="删除" @click="remove(scope.row)" />
            </template>
          </el-table-column>
        </el-table>
        <el-pagination :current-page="query.pageNum" :page-size="query.pageSize" :total="total"
          layout="total, prev, pager, next" @current-change="page => { query.pageNum=page; load() }" />
      </div>
    </table-con>

    <el-dialog :title="form.id ? '编辑API客户端' : '新增API客户端'" :visible.sync="editVisible" width="620px">
      <el-form ref="form" :model="form" :rules="rules" label-width="150px">
        <el-form-item label="客户端名称" prop="clientName"><el-input v-model="form.clientName" maxlength="100" /></el-form-item>
        <el-form-item label="回调地址" prop="redirectUri"><el-input v-model="form.redirectUri" placeholder="https://example.com/oauth/callback" /></el-form-item>
        <el-form-item label="Scope"><el-tag>UserAPI</el-tag></el-form-item>
        <el-form-item label="Access有效期（秒）" prop="accessTokenExpire">
          <div class="duration-row">
            <el-input class="duration-seconds" v-model.number="form.accessTokenExpire" type="number" min="1" @input="secondsChanged('accessTokenExpire')">
              <template slot="append">秒</template>
            </el-input>
            <span class="duration-separator">=</span>
            <el-input class="duration-part" v-model.number="form.accessTokenDays" type="number" min="0" @input="partsChanged('accessTokenExpire')"><template slot="append">天</template></el-input>
            <el-input class="duration-part" v-model.number="form.accessTokenHours" type="number" min="0" max="23.99" step="0.01" @input="partsChanged('accessTokenExpire')"><template slot="append">小时</template></el-input>
          </div>
        </el-form-item>
        <el-form-item label="Refresh有效期（秒）" prop="refreshTokenExpire">
          <div class="duration-row">
            <el-input class="duration-seconds" v-model.number="form.refreshTokenExpire" type="number" min="1" @input="secondsChanged('refreshTokenExpire')">
              <template slot="append">秒</template>
            </el-input>
            <span class="duration-separator">=</span>
            <el-input class="duration-part" v-model.number="form.refreshTokenDays" type="number" min="0" @input="partsChanged('refreshTokenExpire')"><template slot="append">天</template></el-input>
            <el-input class="duration-part" v-model.number="form.refreshTokenHours" type="number" min="0" max="23.99" step="0.01" @input="partsChanged('refreshTokenExpire')"><template slot="append">小时</template></el-input>
          </div>
        </el-form-item>
        <el-form-item label="状态"><el-switch v-model="form.status" active-value="0" inactive-value="1" active-text="启用" inactive-text="停用" /></el-form-item>
      </el-form>
      <span slot="footer"><el-button @click="editVisible=false">取消</el-button><el-button type="primary" @click="save">保存</el-button></span>
    </el-dialog>

    <el-dialog :title="authorizedClient ? `${authorizedClient.clientName} - 授权Token` : '授权Token'" :visible.sync="authorizedVisible" width="900px">
      <el-row :gutter="10" class="authorized-search">
        <el-col :span="8"><el-input v-model="authorizedQuery.userName" clearable size="small" placeholder="按用户名检索" @keyup.enter.native="searchAuthorizedTokens" /></el-col>
        <el-col :span="8"><el-input v-model="authorizedQuery.token" clearable size="small" placeholder="按Token ID检索" @keyup.enter.native="searchAuthorizedTokens" /></el-col>
        <el-col :span="8">
          <el-button size="small" type="primary" @click="searchAuthorizedTokens">搜索</el-button>
          <el-button size="small" @click="resetAuthorizedSearch">重置</el-button>
        </el-col>
      </el-row>
      <el-table :data="authorizedTokens" border v-loading="authorizedLoading">
        <el-table-column label="授权主体" min-width="190"><template slot-scope="scope">{{ scope.row.permanent ? '客户端模式（用户名按请求传入）' : scope.row.userName }}</template></el-table-column>
        <el-table-column prop="userId" label="用户ID" min-width="180" show-overflow-tooltip />
        <el-table-column prop="tokenId" label="Token ID（JTI）" min-width="230" show-overflow-tooltip />
        <el-table-column label="授权时间" width="165"><template slot-scope="scope">{{ formatTokenTime(scope.row.authorizedAt) }}</template></el-table-column>
        <el-table-column label="Access过期时间" width="190">
          <template slot-scope="scope">
            <el-tag v-if="scope.row.permanent" type="success" size="mini">永久</el-tag>
            <el-tag v-else-if="scope.row.expired" type="info" size="mini">已过期</el-tag>
            <span v-if="!scope.row.permanent">{{ formatTokenTime(scope.row.expireTime) }}</span>
          </template>
        </el-table-column>
        <el-table-column label="操作" fixed="right" width="80" align="center">
          <template slot-scope="scope"><el-button type="danger" size="mini" @click="revokeToken(scope.row)">踢出</el-button></template>
        </el-table-column>
      </el-table>
      <el-pagination class="authorized-pagination" :current-page="authorizedQuery.pageNum" :page-size="authorizedQuery.pageSize"
        :page-sizes="[10, 20, 50, 100]" :total="authorizedTotal" layout="total, sizes, prev, pager, next, jumper"
        @current-change="authorizedPageChanged" @size-change="authorizedPageSizeChanged" />
      <span slot="footer"><el-button @click="authorizedVisible=false">关闭</el-button></span>
    </el-dialog>

  </div>
</template>

<script>
import { TableResize } from '@/assets/js/utils'

export default {
  name: 'openApiClients',
  data() {
    return {
      query: { name: '', pageNum: 1, pageSize: 10 }, rows: [], total: 0,
      tableResize: new TableResize(260),
      editVisible: false,
      authorizedVisible: false, authorizedLoading: false, authorizedClient: null, authorizedTokens: [], authorizedTotal: 0,
      authorizedQuery: { userName: '', token: '', pageNum: 1, pageSize: 10 },
      form: this.emptyForm(),
      rules: {
        clientName: [{ required: true, message: '请输入客户端名称', trigger: 'blur' }],
        redirectUri: [{ required: true, message: '请输入回调地址', trigger: 'blur' }],
        accessTokenExpire: [{ required: true, message: '请输入有效期秒数' }],
        refreshTokenExpire: [{ required: true, message: '请输入有效期秒数' }]
      }
    }
  },
  created() { this.load() },
  mounted() { this.tableResize.onRun() },
  destroyed() { this.tableResize.onStop() },
  methods: {
    emptyForm() { return { id: null, clientName: '', redirectUri: '', scopeText: 'UserAPI', accessTokenExpire: 1800, refreshTokenExpire: 2592000, accessTokenDays: 0, accessTokenHours: 0.5, refreshTokenDays: 30, refreshTokenHours: 0, status: '0' } },
    scopeLines(value) { return ['UserAPI'] },
    parseScopes(value) { return ['UserAPI'] },
    async load() {
      const { data: res } = await this.$http.get('oauth/client/list', { params: this.query })
      if (res.code === 200) { this.rows = res.data.list; this.total = res.data.total } else this.$message.error(res.message)
    },
    openCreate() { this.form = this.emptyForm(); this.editVisible = true; this.$nextTick(() => this.$refs.form && this.$refs.form.clearValidate()) },
    async showAuthorizedTokens(client) {
      this.authorizedClient = client
      this.authorizedQuery = { userName: '', token: '', pageNum: 1, pageSize: 10 }
      this.authorizedVisible = true
      await this.loadAuthorizedTokens()
    },
    async loadAuthorizedTokens() {
      this.authorizedLoading = true
      try {
        const { data: res } = await this.$http.get(`oauth/client/${this.authorizedClient.id}/authorized-tokens`, { params: this.authorizedQuery })
        if (res.code === 200) {
          this.authorizedTokens = res.data.list || []
          this.authorizedTotal = res.data.total || 0
        }
        else this.$message.error(res.message)
      } finally { this.authorizedLoading = false }
    },
    searchAuthorizedTokens() { this.authorizedQuery.pageNum = 1; this.loadAuthorizedTokens() },
    resetAuthorizedSearch() { this.authorizedQuery.userName = ''; this.authorizedQuery.token = ''; this.searchAuthorizedTokens() },
    authorizedPageChanged(page) { this.authorizedQuery.pageNum = page; this.loadAuthorizedTokens() },
    authorizedPageSizeChanged(size) { this.authorizedQuery.pageSize = size; this.authorizedQuery.pageNum = 1; this.loadAuthorizedTokens() },
    formatTokenTime(timestamp) { return timestamp ? new Date(timestamp).toLocaleString() : '-' },
    revokeToken(token) {
      this.$confirm(`确认踢出用户“${token.userName}”的Token ${token.tokenId}？该Token及其关联的Refresh Token将失效。`, '踢出授权Token', { type: 'warning' }).then(async () => {
        const { data: res } = await this.$http.delete(`oauth/client/${this.authorizedClient.id}/authorized-tokens/${encodeURIComponent(token.tokenId)}`)
        if (res.code === 200) {
          this.$message.success('Token已踢出')
          if (this.authorizedTokens.length === 1 && this.authorizedQuery.pageNum > 1) this.authorizedQuery.pageNum--
          await this.loadAuthorizedTokens()
        }
        else this.$message.error(res.message)
      }).catch(() => {})
    },
    openEdit(row) {
      const accessDays = Math.floor(row.accessTokenExpire / 86400)
      const refreshDays = Math.floor(row.refreshTokenExpire / 86400)
      this.form = { id: row.id, clientName: row.clientName, redirectUri: row.redirectUri, scopeText: 'UserAPI', accessTokenExpire: row.accessTokenExpire, refreshTokenExpire: row.refreshTokenExpire, accessTokenDays: accessDays, accessTokenHours: Number(((row.accessTokenExpire - accessDays * 86400) / 3600).toFixed(2)), refreshTokenDays: refreshDays, refreshTokenHours: Number(((row.refreshTokenExpire - refreshDays * 86400) / 3600).toFixed(2)), status: row.status }
      this.editVisible = true; this.$nextTick(() => this.$refs.form && this.$refs.form.clearValidate())
    },
    save() {
      this.$refs.form.validate(async valid => {
        if (!valid) return
        if (!Number.isFinite(Number(this.form.accessTokenExpire)) || Number(this.form.accessTokenExpire) <= 0
          || !Number.isFinite(Number(this.form.refreshTokenExpire)) || Number(this.form.refreshTokenExpire) <= 0) {
          return this.$message.error('有效期秒数必须大于0')
        }
        if (this.form.refreshTokenExpire <= this.form.accessTokenExpire) return this.$message.error('Refresh有效期必须大于Access有效期')
        const payload = { ...this.form, scope: this.scopeLines(this.form.scopeText) }; delete payload.scopeText
        delete payload.accessTokenDays; delete payload.accessTokenHours; delete payload.refreshTokenDays; delete payload.refreshTokenHours
        const response = this.form.id ? await this.$http.put('oauth/client', payload) : await this.$http.post('oauth/client', payload)
        const res = response.data
        if (res.code !== 200) return this.$message.error(res.message)
        this.editVisible = false; await this.load()
        this.$message.success(this.form.id ? '保存成功' : '创建成功，Client Secret可在列表中查看')
      })
    },
    resetSecret(row) {
      this.$confirm('旧密钥将立即不能用于换取新Token，是否继续？', '重置密钥', { type: 'warning' }).then(async () => {
        const { data: res } = await this.$http.post(`oauth/client/${row.id}/secret`)
        if (res.code === 200) { await this.load(); this.$message.success('密钥已重置') } else this.$message.error(res.message)
      }).catch(() => {})
    },
    remove(row) {
      this.$confirm('确认删除该API客户端？', '提示', { type: 'warning' }).then(async () => {
        const { data: res } = await this.$http.delete(`oauth/client/${row.id}`)
        if (res.code === 200) { this.$message.success('删除成功'); this.load() } else this.$message.error(res.message)
      }).catch(() => {})
    },
    secondsChanged(field) {
      const prefix = field === 'accessTokenExpire' ? 'accessToken' : 'refreshToken'
      const seconds = Number(this.form[field])
      const days = Number.isFinite(seconds) && seconds > 0 ? Math.floor(seconds / 86400) : 0
      this.form[prefix + 'Days'] = days
      this.form[prefix + 'Hours'] = Number.isFinite(seconds) && seconds > 0
        ? Number(((seconds - days * 86400) / 3600).toFixed(2)) : 0
    },
    partsChanged(field) {
      const prefix = field === 'accessTokenExpire' ? 'accessToken' : 'refreshToken'
      const days = Math.max(0, Number(this.form[prefix + 'Days']) || 0)
      const hours = Math.max(0, Number(this.form[prefix + 'Hours']) || 0)
      this.form[field] = Math.floor(days * 86400 + hours * 3600)
    }
  }
}
</script>

<style scoped>
.actions { text-align: right; padding-right: 10px; }
.scope-tag { margin: 2px 4px 2px 0; }
.hint { color: #909399; font-size: 12px; line-height: 20px; }
.duration-row { display: flex; align-items: center; gap: 8px; }
.duration-row .duration-seconds { width: 145px; }
.duration-row .duration-part { width: 115px; }
.duration-separator { flex: none; color: #909399; }
.authorized-search { margin-bottom: 12px; }
.authorized-pagination { margin-top: 12px; text-align: right; }
.client-table { height: 100%; }
.client-table .el-table { flex: 1; }
.client-table .el-pagination { height: 35px; line-height: 35px; text-align: center; flex: none; }
</style>